import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { EditLock } from '@core/types/api/EditLock/EditLock';
import {
  type EditLocksChangedEventWireFormat,
  NewEditLocksChangedEventFromWireFormat,
} from '@core/types/api/EditLocksChangedEvent/EditLocksChangedEvent';
import Enums from '@core/types/api/Enums';
import { hasValue } from '@core/utils/equality';
import { isValidUUID } from '@core/utils/uuid';
import {
  EDIT_LOCK_EXPIRY_GRACE_MS,
  EDIT_LOCK_HEARTBEAT_MS,
  type EditLockRoutes,
} from '@web/constants/editLock';
import { pageClientId } from '@web/utils/pageClientId';
import { releaseEditLocksOnPageHide } from '@web/utils/releaseEditLocksOnPageHide';
import { SlabConfig } from '@web/utils/SlabConfig';

import { useInvalidateCache } from './useInvalidateCache';
import { useSlabAuth } from './useSlabAuth';
import { useSlabMutation } from './useSlabMutation';
import { useSlabQuery } from './useSlabQuery';
import { useSlabSocket } from './useSlabSocket';

/** Which of the records have an active edit lock right now, kept live over the websocket. */
export const useEditLockState = (
  routes: EditLockRoutes,
  ids: string[],
): { locks: EditLock[]; lockedByOthers: EditLock[]; isLoaded: boolean } => {
  const ctx = useContext(SlabContext);
  const { invalidateCache } = useInvalidateCache();
  const idsKey = ids.filter(isValidUUID).join(',');
  const validIds = useMemo(() => (idsKey === '' ? [] : idsKey.split(',')), [idsKey]);

  const { data, isSuccess } = useSlabQuery(
    routes.list,
    { queryParams: { id: validIds } },
    {
      enabled: validIds.length > 0,
      // The expiry timer fires once, so a failed read would otherwise never be retried.
      refetchInterval: (query) => (query.state.status === 'error' ? EDIT_LOCK_HEARTBEAT_MS : false),
    },
  );
  const locks = data ?? [];

  useSlabSocket(
    () => [
      {
        method: Enums.WsEvent.EditLocksChanged,
        action: (payload): void => {
          const event = NewEditLocksChangedEventFromWireFormat(
            payload as EditLocksChangedEventWireFormat,
          );
          if (
            event.entity === routes.entity &&
            event.entityIds.some((id) => validIds.includes(id))
          ) {
            invalidateCache([routes.list]);
          }
        },
      },
      {
        method: Enums.WsEvent.Reconnected,
        action: (): void => invalidateCache([routes.list]),
      },
    ],
    [routes.entity, routes.list, validIds, invalidateCache],
  );

  // A holder that vanished sends no release event, so the lease runs out silently.
  const soonestExpiry =
    locks.length > 0 ? Math.min(...locks.map((lock) => lock.expiresAt.toMillis())) : null;
  useEffect(() => {
    if (!hasValue(soonestExpiry)) return undefined;

    const msUntilExpiry = Math.max(soonestExpiry - Date.now(), 0);
    const timer = window.setTimeout(
      () => invalidateCache([routes.list]),
      msUntilExpiry + EDIT_LOCK_EXPIRY_GRACE_MS,
    );

    return (): void => window.clearTimeout(timer);
  }, [soonestExpiry, routes.list, invalidateCache]);

  return {
    locks,
    lockedByOthers: locks.filter((lock) => lock.editorId !== ctx.userInfo.user.id),
    isLoaded: isSuccess,
  };
};

export enum EditLockStatus {
  Inactive = 'inactive',
  Acquiring = 'acquiring',
  Held = 'held',
  Blocked = 'blocked',
}

type EditLockOwnership = {
  status: EditLockStatus;
  /** The records another editor holds, with their holders; any of them makes the status Blocked. */
  blocked: EditLock[];
};

/** Holds the edit locks while `enabled`, renewing them on a heartbeat and releasing them on close. */
export const useEditLockOwnership = (
  routes: EditLockRoutes,
  ids: string[],
  enabled: boolean,
  onUnblocked?: () => void,
): EditLockOwnership => {
  const [status, setStatus] = useState(EditLockStatus.Inactive);
  const [blocked, setBlocked] = useState<EditLock[]>([]);
  const isWaiting = status === EditLockStatus.Blocked;
  const blockedIds = blocked.map((lock) => lock.entityId);
  const { locks, isLoaded } = useEditLockState(routes, isWaiting ? blockedIds : []);
  const hasFreedLock = isWaiting && isLoaded && locks.length < blockedIds.length;
  const onUnblockedRef = useRef(onUnblocked);
  onUnblockedRef.current = onUnblocked;
  const retakeLocksRef = useRef((): void => undefined);

  const { getAccessTokenSilently } = useSlabAuth();
  const acquireLocks = useSlabMutation(routes.acquire);
  const releaseLocks = useSlabMutation(routes.release);
  const idsKey = ids.filter(isValidUUID).join(',');

  useEffect(() => {
    if (!enabled || idsKey === '') return undefined;

    const requestedIds = idsKey.split(',');
    let isActive = true;
    let isBlocked = false;
    let heldIds: string[] = [];
    let pendingAcquire: Promise<void> = Promise.resolve();
    // pagehide cannot wait for a token, so keep the latest one at hand.
    let accessToken: string | null = null;

    const takeLocks = (): void => {
      pendingAcquire = acquireLocks
        .mutateAsync({ args: { body: { ids: requestedIds, clientId: pageClientId } } })
        .then(
          (acquisition): void => {
            const wasBlocked = isBlocked;
            const refused = acquisition.blocked;
            isBlocked = refused.length > 0;
            heldIds = acquisition.held.map((lock) => lock.entityId);
            if (!isActive) return;

            setBlocked((current) =>
              current.length === 0 && refused.length === 0 ? current : refused,
            );
            if (isBlocked) setStatus(EditLockStatus.Blocked);
            else if (heldIds.length > 0) setStatus(EditLockStatus.Held);
            if (wasBlocked && !isBlocked) onUnblockedRef.current?.();
            getAccessTokenSilently({
              authorizationParams: { audience: SlabConfig.auth0.audience },
            })
              .then((token: string): void => {
                accessToken = token;
              })
              .catch((): void => {});
          },
          // The next heartbeat retries.
          (): void => {},
        );
    };

    setStatus(EditLockStatus.Acquiring);
    takeLocks();
    retakeLocksRef.current = takeLocks;
    const heartbeat = window.setInterval(takeLocks, EDIT_LOCK_HEARTBEAT_MS);

    // Closing the tab skips the unmount cleanup; keepalive lets the release outlive the page.
    const releaseOnPageHide = (): void => {
      if (heldIds.length === 0 || !hasValue(accessToken)) return;

      releaseEditLocksOnPageHide(routes.release, heldIds, accessToken);
    };
    window.addEventListener('pagehide', releaseOnPageHide);

    return (): void => {
      isActive = false;
      retakeLocksRef.current = (): void => undefined;
      window.clearInterval(heartbeat);
      window.removeEventListener('pagehide', releaseOnPageHide);
      setStatus(EditLockStatus.Inactive);
      setBlocked([]);
      // A request still in flight can grant the locks after this point, so release only once it settles.
      pendingAcquire.then((): void => {
        if (heldIds.length > 0) {
          releaseLocks.mutate({ args: { body: { ids: heldIds, clientId: pageClientId } } });
        }
      });
    };
  }, [enabled, idsKey, routes]);

  useEffect(() => {
    if (hasFreedLock) retakeLocksRef.current();
  }, [hasFreedLock]);

  return { status, blocked };
};
